import { useEffect, useImperativeHandle, useRef, useState, forwardRef } from 'react';
import { Button } from './ui';

export interface SignaturePadHandle {
  /** PNG data URL, or null when nothing has been drawn. */
  toDataUrl: () => string | null;
  clear: () => void;
}

/**
 * Pointer-drawn signature, exported as a PNG data URL — the same shape the
 * backend already accepts from the mobile app's signature canvas.
 */
const SignaturePad = forwardRef<SignaturePadHandle, { label?: string; height?: number }>(function SignaturePad(
  { label = 'Sign here', height = 150 },
  ref,
) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    // Match the backing store to the CSS box so strokes are not blurry.
    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = Math.max(1, rect.width * ratio);
    canvas.height = Math.max(1, height * ratio);
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#ECEDEE';
  }, [height]);

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (canvas && ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
    setDirty(false);
  };

  useImperativeHandle(ref, () => ({
    toDataUrl: () => (dirty && canvasRef.current ? exportForPaper(canvasRef.current) : null),
    clear,
  }));

  const point = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  return (
    <div>
      <div className="mb-1.5 flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wider text-dim">{label}</span>
        <Button type="button" variant="ghost" onClick={clear} className="px-2 py-0.5 text-xs">
          Clear
        </Button>
      </div>
      <canvas
        ref={canvasRef}
        aria-label={label}
        role="img"
        style={{ height, touchAction: 'none' }}
        className="w-full cursor-crosshair rounded-lg border border-border bg-bg"
        onPointerDown={(e) => {
          e.currentTarget.setPointerCapture(e.pointerId);
          const ctx = e.currentTarget.getContext('2d');
          if (!ctx) return;
          const { x, y } = point(e);
          ctx.beginPath();
          ctx.moveTo(x, y);
          drawing.current = true;
          setDirty(true);
        }}
        onPointerMove={(e) => {
          if (!drawing.current) return;
          const ctx = e.currentTarget.getContext('2d');
          if (!ctx) return;
          const { x, y } = point(e);
          ctx.lineTo(x, y);
          ctx.stroke();
        }}
        onPointerUp={() => {
          drawing.current = false;
        }}
        onPointerLeave={() => {
          drawing.current = false;
        }}
      />
      {!dirty && <p className="mt-1 text-xs text-dim">Draw your signature with a mouse, trackpad or stylus.</p>}
    </div>
  );
});

export default SignaturePad;

/**
 * The pad draws light strokes so they show on the dark UI, but the signature
 * lands on white paper, where #ECEDEE is all but invisible. Export re-inks the
 * strokes near-black and crops to the signature itself, so the PDF gets a
 * legible, correctly proportioned mark instead of a faint sliver of a very
 * wide, mostly empty canvas.
 */
const PAPER_INK = '#111827';

export function exportForPaper(source: HTMLCanvasElement): string {
  try {
    return inkAndCrop(source);
  } catch {
    // Any canvas API missing (old browser, test DOM): the raw pad beats no signature.
    return source.toDataURL('image/png');
  }
}

function inkAndCrop(source: HTMLCanvasElement): string {
  const ctx = source.getContext('2d');
  if (!ctx) return source.toDataURL('image/png');
  const { width, height } = source;
  const pixels = ctx.getImageData(0, 0, width, height).data;
  let minX = width, minY = height, maxX = -1, maxY = -1;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (pixels[(y * width + x) * 4 + 3] > 0) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }
  if (maxX < 0) return source.toDataURL('image/png');
  const pad = Math.round(8 * (window.devicePixelRatio || 1));
  const x0 = Math.max(0, minX - pad);
  const y0 = Math.max(0, minY - pad);
  const w = Math.min(width, maxX + pad + 1) - x0;
  const h = Math.min(height, maxY + pad + 1) - y0;
  const out = document.createElement('canvas');
  out.width = w;
  out.height = h;
  const octx = out.getContext('2d');
  if (!octx) return source.toDataURL('image/png');
  octx.drawImage(source, x0, y0, w, h, 0, 0, w, h);
  // Keep the stroke shapes, replace their colour.
  octx.globalCompositeOperation = 'source-in';
  octx.fillStyle = PAPER_INK;
  octx.fillRect(0, 0, w, h);
  return out.toDataURL('image/png');
}

